// Client-side image normalization for patient photos. We downscale to a bounded
// square-ish JPEG before upload so: (a) uploads stay small over clinic wifi/4G,
// (b) the stored content-type is always one the server allowlists, and (c) the
// server-side size backstop is essentially never hit. The resulting Blob is what
// gets PUT to the presigned S3 URL.

export const PHOTO_MAX_EDGE = 800;
export const PHOTO_JPEG_QUALITY = 0.85;
export const PHOTO_CONTENT_TYPE = 'image/jpeg';

/** Compute target dimensions that fit within `maxEdge` while preserving ratio. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Draw a source (image or video frame) onto a canvas, downscaled, and export a JPEG Blob. */
async function drawToJpeg(
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
): Promise<Blob> {
  const { width, height } = fitWithin(srcW, srcH, PHOTO_MAX_EDGE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(source, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, PHOTO_CONTENT_TYPE, PHOTO_JPEG_QUALITY),
  );
  if (!blob) throw new Error('Failed to encode photo');
  return blob;
}

/** Snap the current frame of a playing <video> to a downscaled JPEG Blob. */
export function captureVideoFrame(video: HTMLVideoElement): Promise<Blob> {
  return drawToJpeg(video, video.videoWidth, video.videoHeight);
}

/** Decode a user-selected image File and re-encode it as a downscaled JPEG Blob. */
export function fileToJpeg(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = async () => {
      try {
        const blob = await drawToJpeg(img, img.naturalWidth, img.naturalHeight);
        resolve(blob);
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read the selected image'));
    };
    img.src = url;
  });
}
